import { useCallback } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { MapPin, Truck, X } from 'lucide-react'
import { Button } from '@/components/ds2'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import type { DriverClusterInspection, StateActivity } from '../types/boxTruckMap'

interface BoxTruckMapActivityPanelProps {
    states: StateActivity[]
    selectedState: string | null
    selectedDriverId: string | null
    inspection: DriverClusterInspection | null
    loadingStateCodes: ReadonlySet<string>
    failedStateCodes: ReadonlySet<string>
    onSelectState: (stateName: string) => void
    onSelectDriver: (driver: BoxTruckMapDriver) => void
    onCloseInspection: () => void
    onViewStateLoads: (stateCode: string) => void
    onRetryStateLoads: (stateCode: string) => void
}

export const BoxTruckMapActivityPanel = ({
    states,
    selectedState,
    selectedDriverId,
    inspection,
    loadingStateCodes,
    failedStateCodes,
    onSelectState,
    onSelectDriver,
    onCloseInspection,
    onViewStateLoads,
    onRetryStateLoads,
}: BoxTruckMapActivityPanelProps) => {
    const activeState = selectedState ? states.find((state) => state.name === selectedState) : null
    const activeStateLoading = activeState ? loadingStateCodes.has(activeState.code) : false
    const renderDriver = useCallback(
        (_index: number, driver: BoxTruckMapDriver) => (
            <button
                type="button"
                onClick={() => onSelectDriver(driver)}
                className={`flex min-h-12 w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selectedDriverId === driver.driverId ? 'bg-accent-soft' : ''}`}
            >
                <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${driver.verified ? 'bg-success-soft text-success-soft-foreground' : 'bg-warning-soft text-warning-soft-foreground'}`}
                >
                    <Truck size={15} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">{driver.name || 'Unnamed driver'}</span>
                    <span className="block truncate text-xs text-foreground-muted">
                        {[driver.equipment, driver.truckNumber ? `Truck #${driver.truckNumber.replace(/^#/, '')}` : null].filter(Boolean).join(' · ') ||
                            'Equipment not provided'}
                    </span>
                </span>
            </button>
        ),
        [onSelectDriver, selectedDriverId]
    )

    if (inspection) {
        return (
            <aside className="flex min-h-[560px] flex-col rounded-2xl bg-surface p-4 shadow-surface">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-base font-semibold">Drivers in this area</h2>
                        <p className="mt-1 text-sm text-foreground-muted">
                            {inspection.drivers.length < inspection.totalCount
                                ? `Showing ${inspection.drivers.length.toLocaleString()} of ${inspection.totalCount.toLocaleString()} grouped drivers.`
                                : `${inspection.totalCount.toLocaleString()} ${inspection.totalCount === 1 ? 'driver is' : 'drivers are'} grouped here.`}
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" iconOnly aria-label="Close driver list" onClick={onCloseInspection}>
                        <X size={16} aria-hidden />
                    </Button>
                </div>
                <Virtuoso className="mt-3 min-h-0 flex-1" data={inspection.drivers} itemContent={renderDriver} />
            </aside>
        )
    }

    return (
        <aside className="flex min-h-[560px] flex-col rounded-2xl bg-surface p-4 shadow-surface">
            <h2 className="text-base font-semibold">{activeState?.name ?? 'State activity'}</h2>
            <p className="mt-1 text-sm text-foreground-muted">
                {activeStateLoading
                    ? `Loading active loads · ${activeState?.drivers.toLocaleString() ?? 0} fresh drivers`
                    : activeState && activeState.loads !== null
                      ? `${activeState.loads.toLocaleString()} active loads · ${activeState.drivers.toLocaleString()} fresh drivers`
                      : activeState
                        ? `Load count not loaded · ${activeState.drivers.toLocaleString()} fresh drivers`
                        : 'Select a state to load its active loads.'}
            </p>
            {activeState && failedStateCodes.has(activeState.code) && !activeStateLoading && (
                <div className="mt-2 flex items-center gap-2">
                    <p className="text-xs text-danger">Couldn’t load this state.</p>
                    <Button variant="ghost" size="sm" onClick={() => onRetryStateLoads(activeState.code)}>
                        Retry
                    </Button>
                </div>
            )}
            {activeState && activeState.loads !== null && activeState.loads > 0 ? (
                <Button variant="primary" size="sm" className="mt-4 self-start" onClick={() => onViewStateLoads(activeState.code)}>
                    View loads
                </Button>
            ) : null}
            <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
                {states.map((state) => {
                    const selected = selectedState === state.name
                    const loadingLoads = loadingStateCodes.has(state.code)
                    return (
                        <button
                            key={state.name}
                            type="button"
                            onClick={() => onSelectState(state.name)}
                            className={`flex w-full items-center justify-between gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selected ? 'bg-accent-soft' : ''}`}
                            aria-pressed={selected}
                        >
                            <span className="inline-flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
                                <MapPin size={14} className="shrink-0" aria-hidden /> <span className="truncate">{state.name}</span>
                            </span>
                            <span className="shrink-0 text-right text-xs text-foreground-muted">
                                <span className="block">
                                    {loadingLoads ? 'Loading loads…' : state.loads === null ? 'Select for loads' : `${state.loads.toLocaleString()} loads`}
                                </span>
                                <span className="block">{state.drivers.toLocaleString()} drivers</span>
                            </span>
                        </button>
                    )
                })}
                {states.length === 0 && <p className="py-4 text-sm text-foreground-muted">No active loads or fresh drivers.</p>}
            </div>
        </aside>
    )
}
